import { isAxiosError } from 'axios'
import {
  documentPageMetadataUserEditStatusSchema,
  DocumentPageMetadataUserEditValuesSchema,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { EvalPagePool } from 'features/knowledgeDocumentViewer/utils/pageMetadataEvalPool'
import { z } from 'zod'

export const METADATA_EVAL_NEXT_PAGES_PATH =
  '/api/v2/documents/metadata-eval/next-pages'

export const NEXT_EVAL_PAGES_FAILURE_MESSAGE = 'Next-page sampling failed'

export const MetadataEvalSampledPageSchema = z.object({
  document_id: z.string().min(1),
  page_id: z.string().min(1),
  page_num: z.number().int().positive(),
  document_type: z.string().nullable().optional(),
  label_status: documentPageMetadataUserEditStatusSchema.nullable().optional(),
  labeled_by: z.string().nullable().optional(),
  blind_qa: z.boolean().optional(),
  values: DocumentPageMetadataUserEditValuesSchema.nullable().optional(),
})

const MetadataEvalNextPagesResponseSchema = z.object({
  pages: z.array(MetadataEvalSampledPageSchema),
})

export async function fetchBrowseEvalPages(
  args: Omit<FetchNextEvalPagesArgs, 'limit' | 'excludePageIds' | 'previewMode'>
): Promise<MetadataEvalSampledPage[]> {
  if (args.pageClassifications?.length === 0 || args.issuers?.length === 0)
    return []
  const res = await args.axios.post(
    `${args.backendUrl}/api/v2/documents/metadata-eval/browse-pages`,
    {
      site_id: args.siteId,
      pool: args.pool,
      included_page_classifications: args.pageClassifications,
      issuers: args.issuers,
    }
  )
  return MetadataEvalNextPagesResponseSchema.parse(res.data).pages
}

const HttpErrorDetailSchema = z.object({
  detail: z.string().min(1),
})

export type MetadataEvalSampledPage = z.infer<
  typeof MetadataEvalSampledPageSchema
>

export type EvalSamplingClient = {
  post: (url: string, data?: unknown) => Promise<{ data: unknown }>
}

export type FetchNextEvalPagesArgs = {
  axios: EvalSamplingClient
  backendUrl: string
  siteId: string
  pool: EvalPagePool
  excludePageIds: string[]
  previewMode: boolean
  limit?: number
  pageClassifications?: readonly string[]
  issuers?: readonly string[]
}

type MetadataEvalNextPagesRequest = {
  site_id: string
  pool: EvalPagePool
  exclude_page_ids: string[]
  limit: number
  preview_mode: boolean
  page_classifications?: string[]
  issuers?: string[]
}

export function evalSamplingFailureMessage(error: Error): string {
  return error.message.length > 0
    ? error.message
    : NEXT_EVAL_PAGES_FAILURE_MESSAGE
}

export async function fetchNextEvalPages(
  args: FetchNextEvalPagesArgs
): Promise<MetadataEvalSampledPage[]> {
  if (
    (args.pageClassifications !== undefined &&
      args.pageClassifications.length === 0) ||
    (args.issuers !== undefined && args.issuers.length === 0)
  ) {
    return []
  }
  const url = `${args.backendUrl}${METADATA_EVAL_NEXT_PAGES_PATH}`
  const body: MetadataEvalNextPagesRequest = {
    site_id: args.siteId,
    pool: args.pool,
    exclude_page_ids: args.excludePageIds,
    limit: args.limit ?? 1,
    preview_mode: args.previewMode,
  }
  if (
    args.pageClassifications !== undefined &&
    args.pageClassifications.length > 0
  ) {
    body.page_classifications = [...args.pageClassifications]
  }
  if (args.issuers !== undefined && args.issuers.length > 0) {
    body.issuers = [...args.issuers]
  }
  try {
    const res = await args.axios.post(url, body)
    return MetadataEvalNextPagesResponseSchema.parse(res.data).pages
  } catch (error) {
    if (isAxiosError(error)) {
      const parsed = HttpErrorDetailSchema.safeParse(error.response?.data)
      if (parsed.success) throw new Error(parsed.data.detail)
    }
    if (error instanceof Error) {
      throw new Error(evalSamplingFailureMessage(error))
    }
    throw new Error(NEXT_EVAL_PAGES_FAILURE_MESSAGE)
  }
}
